import { Link, useLocation, useRouterState } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useLocale, type Locale } from "@/lib/i18n";
import { quotePath, quoteSearch } from "@/lib/quote-links";
import {
  resolveResourceCommercialContext,
  type ResourceTaxonomyInput,
  type ResourceCluster,
} from "@/lib/resource-commerce";

type Variant = "inline" | "footer";
export type CTAStyle =
  | "blue-glow"
  | "navy-coral"
  | "minimal-light"
  | "institutional-inline"
  | "institutional-dark"
  | "none";

interface ArticleCTAProps {
  variant?: Variant;
  style?: CTAStyle | null;
  eyebrow?: string | null;
  title?: string | null;
  body?: string | null;
  primaryLabel?: string | null;
  /** Kept for backwards-compatible CMS records. Resource CTAs always preserve quote attribution. */
  primaryHref?: string | null;
  onPrimaryClick?: () => void;
  tag?: string;
  /**
   * Forces the commercial cluster instead of inferring it from the loaded resource.
   * Used by the resources index and the topic hubs, which have no article loader.
   */
  cluster?: ResourceCluster | null;
  /** Explicit attribution source when the CTA is not rendered inside an article route. */
  source?: string | null;
  /** Renders a thin hub-colored accent band on top of the dark card. */
  accent?: boolean;
  className?: string;
}

const HUB_ACCENT: Record<ResourceCluster, string> = {
  performance: "var(--hub-performance)",
  conversion: "var(--hub-conversion)",
  "technical-seo": "var(--hub-seo)",
  migration: "var(--hub-migration)",
  development: "var(--hub-development)",
  general: "var(--action-primary)",
};

type ClusterCopy = { eyebrow: string; title: string; body: string };

/**
 * Cluster-specific default copy. CMS values always win; these only replace the
 * previous generic "when the issue requires execution" fallback.
 */
const CLUSTER_COPY: Record<
  Exclude<ResourceCluster, "migration">,
  Record<Locale, { footer: ClusterCopy; inline: ClusterCopy }>
> = {
  performance: {
    en: {
      footer: {
        eyebrow: "Ready to fix your speed?",
        title: "Scope a Shopify performance engagement",
        body: "We measure LCP, INP and CLS on your real templates, isolate what actually costs you seconds, and ship the fixes against a speed budget you can hold suppliers to.",
      },
      inline: {
        eyebrow: "Performance Fix",
        title: "Need this measured on your store?",
        body: "We turn Core Web Vitals problems into a bounded project with baselines, target thresholds and validation after release.",
      },
    },
    es: {
      footer: {
        eyebrow: "¿Listo para arreglar tu velocidad?",
        title: "Cotiza un proyecto de performance Shopify",
        body: "Medimos LCP, INP y CLS en tus plantillas reales, aislamos qué te cuesta segundos de verdad y ejecutamos los arreglos contra un presupuesto de velocidad verificable.",
      },
      inline: {
        eyebrow: "Performance Fix",
        title: "¿Quieres medir esto en tu tienda?",
        body: "Convertimos los problemas de Core Web Vitals en un proyecto acotado con línea base, umbrales objetivo y validación posterior al release.",
      },
    },
  },
  conversion: {
    en: {
      footer: {
        eyebrow: "Ready to fix your funnel?",
        title: "Scope a Shopify CRO engagement",
        body: "We tell you which of these fixes move your number first, with funnel data to back it, before you spend on anything.",
      },
      inline: {
        eyebrow: "CRO & Conversion Fix",
        title: "Need this applied to your funnel?",
        body: "We size the opportunity across PDP, cart and checkout, then ship the changes with a clear read on what actually moved.",
      },
    },
    es: {
      footer: {
        eyebrow: "¿Listo para arreglar tu embudo?",
        title: "Cotiza un proyecto de CRO para Shopify",
        body: "Te decimos cuál de estos arreglos mueve tu número primero, con datos del embudo que lo respalden, antes de que gastes en nada.",
      },
      inline: {
        eyebrow: "CRO y conversión",
        title: "¿Quieres aplicar esto en tu embudo?",
        body: "Dimensionamos la oportunidad en PDP, carrito y checkout, y ejecutamos los cambios con una lectura clara de qué movió el número.",
      },
    },
  },
  "technical-seo": {
    en: {
      footer: {
        eyebrow: "Ready to fix your indexation?",
        title: "Scope a Shopify technical SEO engagement",
        body: "We audit crawling, canonicals, faceted URLs and rendering, then fix what blocks the pages that should be earning revenue.",
      },
      inline: {
        eyebrow: "Technical SEO Fix",
        title: "Need this checked on your store?",
        body: "We turn crawling and indexation issues into a bounded project with a prioritized fix list and post-release verification.",
      },
    },
    es: {
      footer: {
        eyebrow: "¿Listo para arreglar tu indexación?",
        title: "Cotiza un proyecto de SEO técnico Shopify",
        body: "Auditamos crawling, canonicals, URLs facetadas y renderizado, y corregimos lo que bloquea a las páginas que deberían generar ingresos.",
      },
      inline: {
        eyebrow: "SEO técnico",
        title: "¿Quieres revisar esto en tu tienda?",
        body: "Convertimos los problemas de crawling e indexación en un proyecto acotado, con lista priorizada de arreglos y verificación posterior.",
      },
    },
  },
  development: {
    en: {
      footer: {
        eyebrow: "Ready to build it properly?",
        title: "Scope a Shopify build or integration",
        body: "Custom themes, apps, Functions and ERP, OMS or PIM integrations, scoped with deliverables, boundaries and acceptance criteria before a line of code is written.",
      },
      inline: {
        eyebrow: "Custom Build or Integration",
        title: "Need this built on your stack?",
        body: "We scope the build or integration with clear deliverables, exclusions and a validation plan for launch.",
      },
    },
    es: {
      footer: {
        eyebrow: "¿Listo para construirlo bien?",
        title: "Cotiza un desarrollo o integración Shopify",
        body: "Themes custom, apps, Functions e integraciones con ERP, OMS o PIM, con entregables, límites y criterios de aceptación definidos antes de escribir una línea de código.",
      },
      inline: {
        eyebrow: "Desarrollo o integración",
        title: "¿Quieres construir esto en tu stack?",
        body: "Definimos el desarrollo o la integración con entregables claros, exclusiones y un plan de validación para el lanzamiento.",
      },
    },
  },
  general: {
    en: {
      footer: {
        eyebrow: "Ready to turn this into work?",
        title: "Scope a fixed-price Shopify engagement",
        body: "We define deliverables, exclusions, acceptance criteria, price and validation before implementation begins.",
      },
      inline: {
        eyebrow: "Next decision",
        title: "Need to apply this on your store?",
        body: "We turn this kind of problem into a bounded scope with clear deliverables, boundaries, pricing and validation criteria.",
      },
    },
    es: {
      footer: {
        eyebrow: "¿Listo para convertirlo en trabajo?",
        title: "Cotiza un proyecto Shopify con precio fijo",
        body: "Definimos entregables, exclusiones, criterios de aceptación, precio y validación antes de implementar.",
      },
      inline: {
        eyebrow: "Siguiente decisión",
        title: "¿Necesitas aplicar esto en tu tienda?",
        body: "Convertimos este tipo de problema en un scope acotado, con entregables, límites, precio y criterios de validación claros.",
      },
    },
  },
};

const LEGACY_COPY = /revenue diagnostic|ai revenue engine|revenue engine/i;

function usable(value: string | null | undefined): string | undefined {
  const cleaned = value?.trim();
  if (!cleaned || LEGACY_COPY.test(cleaned)) return undefined;
  return cleaned;
}

function useLoadedResource(): ResourceTaxonomyInput | undefined {
  return useRouterState({
    select: (state) => {
      for (let index = state.matches.length - 1; index >= 0; index -= 1) {
        const loaderData = state.matches[index]?.loaderData as
          | { resource?: ResourceTaxonomyInput }
          | undefined;
        if (loaderData?.resource) return loaderData.resource;
      }
      return undefined;
    },
  });
}

export function ArticleCTA({
  variant = "footer",
  style = "blue-glow",
  eyebrow,
  title,
  body,
  primaryLabel,
  onPrimaryClick,
  tag,
  cluster,
  source,
  accent = false,
  className = "",
}: ArticleCTAProps) {
  const locale = useLocale();
  const location = useLocation();
  const loadedResource = useLoadedResource();
  const isEs = locale === "es";
  const fallbackTags = tag ? [tag] : [];

  if (style === "none") return null;

  const pathname = source ?? location.pathname;
  const context = resolveResourceCommercialContext({
    locale,
    pathname,
    resource: cluster
      ? { cluster }
      : {
          ...loadedResource,
          slug: loadedResource?.slug ?? location.pathname.split("/").filter(Boolean).at(-1),
          tags_en: loadedResource?.tags_en?.length ? loadedResource.tags_en : fallbackTags,
          tags_es: loadedResource?.tags_es?.length ? loadedResource.tags_es : fallbackTags,
        },
  });

  const isFooter = variant === "footer";
  const isMigration = context.cluster === "migration";
  const isPlusMigration = context.migrationMode === "plus";
  const defaultCopy = isMigration
    ? isPlusMigration
      ? isEs
        ? {
            eyebrow: isFooter ? "Migración a Shopify Plus" : context.offerLabel,
            title: "¿Estás planeando una migración a Shopify Plus?",
            body:
              "Definimos un programa de migración para B2B, checkout extensibility, Functions, Markets complejos, múltiples tiendas, integraciones enterprise, QA, lanzamiento y estabilización.",
          }
        : {
            eyebrow: isFooter ? "Shopify Plus migration" : context.offerLabel,
            title: "Planning a Shopify Plus migration?",
            body:
              "We scope a migration program for B2B, checkout extensibility, Functions, complex Markets, multiple stores, enterprise integrations, QA, launch, and stabilization.",
          }
      : isEs
        ? {
            eyebrow: isFooter ? "Migración a Shopify" : context.offerLabel,
            title: "¿Estás planeando una migración a Shopify?",
            body:
              "Definimos y ejecutamos migraciones desde WooCommerce, Magento, BigCommerce, plataformas custom y tiendas Shopify legacy. El destino puede ser Shopify estándar o Shopify Plus, según las necesidades reales de la operación.",
          }
        : {
            eyebrow: isFooter ? "Shopify migration" : context.offerLabel,
            title: "Planning a migration to Shopify?",
            body:
              "We scope and execute migrations from WooCommerce, Magento, BigCommerce, custom platforms, and legacy Shopify builds. The destination can be standard Shopify or Shopify Plus, depending on the actual operating requirements.",
          }
    : CLUSTER_COPY[context.cluster as Exclude<ResourceCluster, "migration">][locale][
        isFooter ? "footer" : "inline"
      ];

  const copy = {
    eyebrow: usable(eyebrow) ?? defaultCopy.eyebrow,
    title: usable(title) ?? defaultCopy.title,
    body: usable(body) ?? defaultCopy.body,
    primary: usable(primaryLabel) ?? context.scopeLabel,
  };

  const containerClass = isFooter
    ? "institutional-cta-dark mt-20 border-white/15 bg-[var(--surface-authority)] p-9 text-[var(--shugert-off-white)] sm:p-12"
    : "institutional-cta-inline my-12 border-[var(--line-neutral)] bg-[var(--surface-secondary)] p-7 text-foreground sm:p-9";
  const eyebrowClass = isFooter ? "text-white/58" : "text-muted-foreground";
  const titleClass = isFooter ? "text-white" : "text-foreground";
  const bodyClass = isFooter ? "text-white/72" : "text-muted-foreground";
  const primaryClass = isFooter
    ? "border border-black/20 bg-[var(--action-primary)] text-[var(--action-primary-text)] hover:brightness-95"
    : "bg-foreground text-background hover:bg-foreground/90";
  const secondaryClass = isFooter
    ? "border border-white/28 text-white hover:border-white/55 hover:bg-white/[0.06]"
    : "border border-foreground/15 text-foreground hover:border-foreground/35 hover:bg-foreground/[0.04]";

  return (
    <section
      aria-label={context.offerLabel}
      data-cms-style={style ?? "blue-glow"}
      data-resource-cluster={context.cluster}
      className={`not-prose relative isolate overflow-hidden rounded-3xl border ${containerClass} ${className}`}
    >
      {isFooter ? <div className="pointer-events-none absolute inset-0 bg-brand-diagonal opacity-65" aria-hidden /> : null}
      {isFooter && accent ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[3px]"
          style={{ background: HUB_ACCENT[context.cluster] }}
        />
      ) : null}
      <div className="relative">
        <p className={`text-[10px] font-semibold uppercase tracking-[0.32em] ${eyebrowClass}`}>
          {copy.eyebrow}
        </p>
        <h3
          className={`mt-4 font-display font-semibold leading-[1.1] tracking-tight ${titleClass} ${isFooter ? "text-3xl sm:text-4xl" : "text-2xl sm:text-3xl"}`}
        >
          {copy.title}
        </h3>
        <p className={`mt-4 max-w-2xl leading-relaxed ${bodyClass} ${isFooter ? "text-base" : "text-sm"}`}>
          {copy.body}
        </p>

        <div className="mt-7 flex flex-wrap items-center gap-3">
          <Link
            to={quotePath(locale)}
            search={quoteSearch({
              source: context.source,
              intent: context.intent,
              challenge: context.challenge,
            })}
            onClick={onPrimaryClick}
            className={`group inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition ${primaryClass}`}
          >
            {copy.primary}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
          <Link
            to={context.serviceHref}
            className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-colors ${secondaryClass}`}
          >
            {context.secondaryCtaLabel}
          </Link>
        </div>
      </div>
    </section>
  );
}